import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Save, ExternalLink, Upload, Key, Camera, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SiteSettings = () => {
  const [settings, setSettings] = useState({
    podcast_name: "",
    tagline: "",
    description: "",
    host_name: "",
    host_bio: "",
    host_image: "",
    logo_url: "",
    podcast_url: "",
    contact_email: ""
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ oldPassword: "", newPassword: "", confirmPassword: "" });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
  });

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error("Failed to fetch settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await axios.put(`${API}/settings`, settings, getAuthHeader());
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_type", "image");

    try {
      const response = await axios.post(`${API}/media/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data"
        }
      });
      handleChange("host_image", response.data.url);
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsChangingPassword(true);
    try {
      await axios.put(
        `${API}/auth/change-password?old_password=${encodeURIComponent(passwordData.oldPassword)}&new_password=${encodeURIComponent(passwordData.newPassword)}`,
        {},
        getAuthHeader()
      );
      toast.success("Password changed successfully");
      setIsPasswordDialogOpen(false);
      setPasswordData({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#2E8B8B]" />
      </div>
    );
  }

  return (
    <div data-testid="site-settings">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[#1A2323] mb-2">Site Settings</h1>
          <p className="text-[#4A5555]">Customize your podcast landing page</p>
        </div>
        <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-[#E5E0D6]" data-testid="change-password-btn">
              <Key className="w-4 h-4 mr-2" />
              Change Password
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Change Password</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="old-password">Current Password</Label>
                <div className="relative">
                  <Input
                    id="old-password"
                    type={showOldPassword ? "text" : "password"}
                    value={passwordData.oldPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                    required
                    data-testid="old-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5555]"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                  >
                    {showOldPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5555]"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsPasswordDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#2E8B8B] hover:bg-[#1F6666] text-white"
                  disabled={isChangingPassword}
                  data-testid="submit-password-btn"
                >
                  {isChangingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Change Password
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Podcast Information */}
        <Card className="border-[#E5E0D6]">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-[#1A2323]">Podcast Information</CardTitle>
            <CardDescription>Basic details about your podcast</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="podcast_name">Podcast Name</Label>
                <Input
                  id="podcast_name"
                  value={settings.podcast_name}
                  onChange={(e) => handleChange("podcast_name", e.target.value)}
                  placeholder="Everyday Echoes"
                  data-testid="settings-podcast-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tagline">Tagline</Label>
                <Input
                  id="tagline"
                  value={settings.tagline}
                  onChange={(e) => handleChange("tagline", e.target.value)}
                  placeholder="Reflecting Life, One Story at a Time"
                  data-testid="settings-tagline"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={settings.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Tell visitors what your podcast is about..."
                rows={4}
                data-testid="settings-description"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="logo_url">Logo URL</Label>
                <Input
                  id="logo_url"
                  value={settings.logo_url}
                  onChange={(e) => handleChange("logo_url", e.target.value)}
                  placeholder="https://example.com/logo.png"
                  data-testid="settings-logo-url"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="podcast_url">Podcast URL (Podbean, etc.)</Label>
                <Input
                  id="podcast_url"
                  value={settings.podcast_url}
                  onChange={(e) => handleChange("podcast_url", e.target.value)}
                  placeholder="https://yourpodcast.podbean.com"
                  data-testid="settings-podcast-url"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Host Information */}
        <Card className="border-[#E5E0D6]">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-[#1A2323]">Host Information</CardTitle>
            <CardDescription>Details about the podcast host - this appears in the "Meet Your Host" section</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Host Image Upload */}
            <div className="space-y-3">
              <Label>Host Profile Picture</Label>
              <div className="flex items-start gap-6">
                <div className="relative group">
                  <div className="w-32 h-32 rounded-xl overflow-hidden bg-[#F5F2EB] border-2 border-dashed border-[#E5E0D6]">
                    {settings.host_image ? (
                      <img 
                        src={settings.host_image} 
                        alt="Host"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Camera className="w-8 h-8 text-[#4A5555]" />
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="host-image-upload"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center"
                  >
                    {isUploading ? (
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    ) : (
                      <Upload className="w-6 h-6 text-white" />
                    )}
                  </button>
                </div>
                <div className="flex-1 space-y-2">
                  <p className="text-sm text-[#4A5555]">
                    Upload a profile picture for the "Meet Your Host" section. Recommended: square image, at least 400x400px.
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      data-testid="upload-host-image-btn"
                    >
                      {isUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload Image
                    </Button>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="host_image" className="text-xs text-[#4A5555]">Or paste image URL:</Label>
                    <Input
                      id="host_image"
                      value={settings.host_image}
                      onChange={(e) => handleChange("host_image", e.target.value)}
                      placeholder="https://example.com/photo.jpg"
                      className="text-sm"
                      data-testid="settings-host-image"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="host_name">Host Name</Label>
                <Input
                  id="host_name"
                  value={settings.host_name}
                  onChange={(e) => handleChange("host_name", e.target.value)}
                  placeholder="Your name"
                  data-testid="settings-host-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => handleChange("contact_email", e.target.value)}
                  placeholder="hello@example.com"
                  data-testid="settings-contact-email"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="host_bio">Host Bio</Label>
              <Textarea
                id="host_bio"
                value={settings.host_bio}
                onChange={(e) => handleChange("host_bio", e.target.value)}
                placeholder="Tell visitors about yourself - your background, what inspired the podcast, and what listeners can expect..."
                rows={5}
                data-testid="settings-host-bio"
              />
            </div>

            {/* Preview */}
            {(settings.host_image || settings.host_name) && (
              <div className="p-4 bg-[#F5F2EB] rounded-xl">
                <p className="text-xs text-[#4A5555] mb-3 uppercase tracking-wider font-medium">Preview</p>
                <div className="flex items-center gap-4">
                  {settings.host_image && (
                    <img 
                      src={settings.host_image} 
                      alt="Host preview"
                      className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                    />
                  )}
                  <div>
                    <p className="font-serif font-medium text-[#1A2323]">{settings.host_name || "Host Name"}</p>
                    <p className="text-sm text-[#4A5555]">Host of {settings.podcast_name || "Everyday Echoes"}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <a 
            href="/" 
            target="_blank" 
            className="text-[#2E8B8B] hover:underline flex items-center gap-1"
          >
            View Live Site <ExternalLink className="w-4 h-4" />
          </a>
          <Button 
            type="submit" 
            className="bg-[#2E8B8B] hover:bg-[#1F6666] text-white"
            disabled={isSaving}
            data-testid="save-settings-btn"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SiteSettings;
